import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CloseButton from 'components/shared/CloseButton';
import DataTable from 'components/shared/DataTable';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import Search from 'components/shared/Search';
import { DEFAULT_PCC_DESTINATION_PROVIDER_TYPES } from 'constants/importConfigurations';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { Classification } from 'models/flexible/Classification';
import type { Group } from 'models/flexible/Group';
import { GroupType } from 'models/flexible/GroupType';
import { Location } from 'models/flexible/Location';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { adminIndexGroups } from 'services/api/admin/groups';
import { groupTypesQueryKeys, indexGroupTypes } from 'services/api/admin/groupTypes';
import { adminIndexLocations } from 'services/api/admin/locations';
import type { AdminGroupIndexFields, AdminLocationIndexFields } from 'services/api/fields';
import type { GroupTypeFields } from 'services/api/fields';
import { normalizePortalString } from 'services/api/portalMappings';
import { colors } from 'styles/theme/colors';
import { LabelBold } from 'styles/typography';
import TrashIcon from 'svg/TrashIcon';

export enum LookupName {
  Groups = 'groups',
  GroupTypes = 'group_types',
  Episodes = 'episodes',
  Locations = 'locations',
  PlanTypes = 'plan_types',
  PccDestinationProviderTypes = 'pcc_destination_provider_types',
}

// PCC sends the destination type as text, so these rows are keyed by it rather than by a file value, and there's no
// wildcard: an unmapped destination type must route nowhere
const isDestinationLookup = (lookupName: LookupName) => lookupName === LookupName.PccDestinationProviderTypes;

const sourceLabel = (lookupName: LookupName) =>
  isDestinationLookup(lookupName) ? 'PCC Destination Type' : 'File Value';

// Matched the way olio-api matches it: case and extra spaces don't count
const normalizeKey = normalizePortalString;

type Lookups = Record<string, string | string[]>;

type LookupEntry = {
  fileValue: string;
  olioValue: string | string[];
};

type Props = {
  disabled: boolean;
  lookupName: LookupName;
  lookups: Lookups;
  handleClose: () => void;
  handleApply: (lookupName: LookupName, lookups: Lookups) => void;
};

// ---------------------------------------------------------------------------
// Add Mapping Form
// ---------------------------------------------------------------------------

function AddMappingForm({
  onSave,
  onCancel,
  lookupName,
}: {
  onSave: (mappings: Array<{ fileValue: string; olioValue: string }>) => void;
  onCancel: () => void;
  lookupName: LookupName;
}) {
  const [mappingMode, setMappingMode] = useState<'standard' | 'wildcard'>('standard');
  const [fileValue, setFileValue] = useState('');
  const [olioValue, setOlioValue] = useState<
    | GroupType<GroupTypeFields>
    | Group<AdminGroupIndexFields>
    | Classification
    | Location<AdminLocationIndexFields>
    | null
  >(null);
  const [wildcardMappings, setWildcardMappings] = useState('');

  const groupTypeOpts = useInfiniteOptions({
    queryKey: ['adminGroupTypes', 'lookups'],
    fetchFn: indexGroupTypes,
    params: { sortBy: 'displayName asc' },
  });
  const providerTypeOpts = useInfiniteOptions({
    queryKey: ['adminGroupTypes', 'provider', 'lookups'],
    fetchFn: indexGroupTypes,
    params: { sortBy: 'displayName asc', type: 'provider' },
  });
  const groupOpts = useInfiniteOptions({
    queryKey: ['adminGroups', 'lookups'],
    fetchFn: (params: Parameters<typeof adminIndexGroups>[0], signal?: Parameters<typeof adminIndexGroups>[1]) =>
      adminIndexGroups<'groupType'>(params, signal),
    params: { sortBy: 'name', include: 'groupType' },
  });
  const episodeOpts = useInfiniteOptions({
    queryKey: ['adminClassifications', 'episode', 'lookups'],
    fetchFn: adminIndexClassifications,
    params: { type: 'episode', sortBy: 'name asc' },
  });
  const planTypeOpts = useInfiniteOptions({
    queryKey: ['adminClassifications', 'plan_type', 'lookups'],
    fetchFn: adminIndexClassifications,
    params: { type: 'plan_type', sortBy: 'name asc' },
  });
  const locationOpts = useInfiniteOptions({
    queryKey: ['adminLocations', 'lookups'],
    fetchFn: adminIndexLocations,
    params: { sortBy: 'name asc', archived: false },
  });

  const handleSave = () => {
    if (mappingMode === 'standard') {
      const value = isDestinationLookup(lookupName) ? fileValue.trim() : fileValue;
      if (value && olioValue) {
        onSave([{ fileValue: value, olioValue: olioValue.name }]);
        setFileValue('');
        setOlioValue(null);
      }
    } else {
      const mappings = wildcardMappings
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((value) => ({ fileValue: '*', olioValue: value }));
      if (mappings.length > 0) {
        onSave(mappings);
        setWildcardMappings('');
      }
    }
  };

  return (
    <>
      <FormHeader>Add New Mapping</FormHeader>
      {!isDestinationLookup(lookupName) && (
        <InputGroup title='Mapping Type'>
          <RadioGroup>
            <RadioLabel>
              <input
                type='radio'
                value='standard'
                checked={mappingMode === 'standard'}
                onChange={(e) => setMappingMode(e.target.value as 'standard' | 'wildcard')}
              />
              Standard
            </RadioLabel>
            <RadioLabel>
              <input
                type='radio'
                value='wildcard'
                checked={mappingMode === 'wildcard'}
                onChange={(e) => setMappingMode(e.target.value as 'standard' | 'wildcard')}
              />
              Wildcard
            </RadioLabel>
          </RadioGroup>
        </InputGroup>
      )}

      {mappingMode === 'standard' ? (
        <>
          <InputGroup title={sourceLabel(lookupName)}>
            <Input
              value={fileValue}
              onChange={(e) => setFileValue(e.target.value)}
              placeholder={sourceLabel(lookupName)}
            />
          </InputGroup>

          {isDestinationLookup(lookupName) && (
            <InputGroup title='Referral Provider Type'>
              <Select<GroupType<GroupTypeFields>>
                {...providerTypeOpts}
                value={olioValue as GroupType<GroupTypeFields> | null}
                onChange={(v) => setOlioValue(v)}
                getOptionValue={(o) => o.name}
                getOptionLabel={(o) => o.name}
                placeholder='Provider type'
              />
            </InputGroup>
          )}

          {lookupName === LookupName.Groups && (
            <InputGroup title='Group'>
              <Select<Group<AdminGroupIndexFields<'groupType'>>>
                {...groupOpts}
                value={olioValue as Group<AdminGroupIndexFields<'groupType'>> | null}
                onChange={(v) => setOlioValue(v)}
                getOptionValue={(o) => o.id}
                getOptionLabel={(o) => `${o.name} (${o.groupType?.displayName ?? ''})`}
                placeholder='Mapped Olio Value'
              />
            </InputGroup>
          )}
          {lookupName === LookupName.Episodes && (
            <InputGroup title='Mapped Olio Value'>
              <Select<Classification>
                {...episodeOpts}
                value={olioValue as Classification | null}
                onChange={(v) => setOlioValue(v)}
                getOptionValue={(o) => o.name}
                getOptionLabel={(o) => o.name}
                placeholder='Mapped Olio Value'
              />
            </InputGroup>
          )}
          {lookupName === LookupName.PlanTypes && (
            <InputGroup title='Mapped Olio Value'>
              <Select<Classification>
                {...planTypeOpts}
                value={olioValue as Classification | null}
                onChange={(v) => setOlioValue(v)}
                getOptionValue={(o) => o.name}
                getOptionLabel={(o) => o.name}
                placeholder='Mapped Olio Value'
              />
            </InputGroup>
          )}
          {lookupName === LookupName.Locations && (
            <InputGroup title='Mapped Olio Value'>
              <Select<Location<AdminLocationIndexFields>>
                {...locationOpts}
                value={olioValue as Location<AdminLocationIndexFields> | null}
                onChange={(v) => setOlioValue(v)}
                getOptionValue={(o) => o.name}
                getOptionLabel={(o) => o.name}
                placeholder='Mapped Olio Value'
              />
            </InputGroup>
          )}
          {lookupName === LookupName.GroupTypes && (
            <InputGroup title='Mapped Olio Value'>
              <Select<GroupType<GroupTypeFields>>
                {...groupTypeOpts}
                value={olioValue as GroupType<GroupTypeFields> | null}
                onChange={(v) => setOlioValue(v)}
                getOptionValue={(o) => o.name}
                getOptionLabel={(o) => o.name}
                placeholder='Mapped Olio Value'
              />
            </InputGroup>
          )}
        </>
      ) : (
        <InputGroup title='Wildcard Mappings'>
          <StyledTextarea
            value={wildcardMappings}
            onChange={(e) => setWildcardMappings(e.target.value)}
            placeholder='Add wildcard mappings here. Separate each mapping with a line break.'
            rows={6}
          />
        </InputGroup>
      )}

      <FormButtonGroup>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={mappingMode === 'standard' ? !fileValue || !olioValue : !wildcardMappings.trim()}>
          Save Mapping
        </Button>
      </FormButtonGroup>
    </>
  );
}

// ---------------------------------------------------------------------------
// Lookups Table
// ---------------------------------------------------------------------------

function LookupsTable({
  data,
  disabled,
  sourceHeader,
  onRemove,
  searchQuery,
  onSearchChange,
  onAddClick,
  onLoadDefaults,
}: {
  data: LookupEntry[];
  disabled: boolean;
  sourceHeader: string;
  onRemove: (fileValue: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAddClick: () => void;
  onLoadDefaults?: () => void;
}) {
  const columnHelper = createColumnHelper<LookupEntry>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('fileValue', {
        header: sourceHeader,
        cell: (info) => (
          <LabelBold>
            <pre style={{ margin: 0 }}>{info.getValue()}</pre>
          </LabelBold>
        ),
      }),
      columnHelper.accessor('olioValue', {
        header: 'Olio Record',
        cell: (info) => {
          const value = info.getValue();
          if (Array.isArray(value)) {
            return (
              <OlioValueCell>
                {value.map((v, i) => (
                  <LabelBold key={i}>{v}</LabelBold>
                ))}
              </OlioValueCell>
            );
          }
          return (
            <OlioValueCell>
              <LabelBold>{value}</LabelBold>
            </OlioValueCell>
          );
        },
      }),
      columnHelper.display({
        id: 'remove',
        header: '',
        size: 24,
        cell: (info) =>
          !disabled && (
            <TrashContainer onClick={() => onRemove(info.row.original.fileValue)}>
              <TrashIcon width={14} height={14} style={{ cursor: 'pointer' }} />
            </TrashContainer>
          ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled, onRemove, sourceHeader]
  );

  return (
    <>
      <SearchBarContainer>
        <Search placeholder='Search...' value={searchQuery} onChange={onSearchChange} />
        <ButtonGroup>
          {onLoadDefaults && (
            <Button variant='ghost' onClick={onLoadDefaults} disabled={disabled}>
              Load default mappings
            </Button>
          )}
          <Button onClick={onAddClick} disabled={disabled}>
            Add New Mapping
          </Button>
        </ButtonGroup>
      </SearchBarContainer>
      <DataTable columns={columns} data={data} defaultSortBy={[{ id: 'fileValue', desc: false }]} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Slideout
// ---------------------------------------------------------------------------

function humanize(str: string) {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function LookupsSlideout({ disabled, lookupName, lookups, handleClose, handleApply }: Props) {
  const [selectedLookups, setSelectedLookups] = useState<Lookups>(lookups || {});
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const providerTypeParams = { type: 'provider', pageSize: 100 };
  const { data: providerTypes } = useQuery({
    queryKey: groupTypesQueryKeys.index(providerTypeParams),
    queryFn: ({ signal }) => indexGroupTypes(providerTypeParams, signal),
    enabled: isDestinationLookup(lookupName),
  });

  const lookupDisplayName = isDestinationLookup(lookupName)
    ? 'Referral Destination Mappings'
    : `${humanize(lookupName).replace(/s$/, '')} Mappings`;

  const tableData = useMemo(() => {
    const entries = selectedLookups
      ? Object.entries(selectedLookups).map(([fileValue, olioValue]) => ({ fileValue, olioValue }))
      : [];
    if (!searchQuery) return entries;
    const query = searchQuery.toLowerCase();
    return entries.filter(
      (e) =>
        e.fileValue.toLowerCase().includes(query) ||
        (typeof e.olioValue === 'string'
          ? e.olioValue.toLowerCase().includes(query)
          : e.olioValue.some((v) => v.toLowerCase().includes(query)))
    );
  }, [selectedLookups, searchQuery]);

  const handleRemove = (key: string) => {
    setSelectedLookups((prev) => {
      const { [key]: _, ...rest } = prev;
      setHasChanges(true);
      return rest;
    });
  };

  const handleAddMapping = (mappings: Array<{ fileValue: string; olioValue: string }>) => {
    setSelectedLookups((prev) => {
      setHasChanges(true);
      const newLookups = { ...prev };
      mappings.forEach(({ fileValue, olioValue }) => {
        // A destination type is text PCC sent, so "*" there is a value like any other, not a wildcard
        if (fileValue === '*' && !isDestinationLookup(lookupName)) {
          const existing = Array.isArray(newLookups['*']) ? (newLookups['*'] as string[]) : [];
          newLookups['*'] = [...existing, olioValue];
        } else {
          // olio-api ignores case and extra spaces, so a row that differs only by those would be a duplicate it
          // silently drops one of
          if (isDestinationLookup(lookupName)) {
            Object.keys(newLookups)
              .filter((key) => normalizeKey(key) === normalizeKey(fileValue))
              .forEach((key) => delete newLookups[key]);
          }
          newLookups[fileValue] = olioValue;
        }
      });
      return newLookups;
    });
    setIsAdding(false);
  };

  // Adds the default rows staff haven't mapped yet, each under this environment's name for its provider type; a row
  // already there is never overwritten
  const handleLoadDefaults = () => {
    const nameByApiName = new Map((providerTypes?.data ?? []).map((type) => [type.apiName, type.name]));

    setSelectedLookups((prev) => {
      const mapped = new Set(Object.keys(prev).map(normalizeKey));
      const missing = Object.entries(DEFAULT_PCC_DESTINATION_PROVIDER_TYPES)
        .filter(
          ([destinationType, apiName]) => !mapped.has(normalizeKey(destinationType)) && nameByApiName.has(apiName)
        )
        .map(([destinationType, apiName]) => [destinationType, nameByApiName.get(apiName)!]);
      if (missing.length === 0) return prev;

      setHasChanges(true);
      return { ...prev, ...Object.fromEntries(missing) };
    });
  };

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>{lookupDisplayName}</PanelTitle>
        <CloseButton onClick={handleClose} />
      </PanelHeader>
      <PanelContent>
        {isAdding ? (
          <AddMappingForm onSave={handleAddMapping} onCancel={() => setIsAdding(false)} lookupName={lookupName} />
        ) : (
          <LookupsTable
            data={tableData}
            sourceHeader={sourceLabel(lookupName)}
            onLoadDefaults={isDestinationLookup(lookupName) && providerTypes ? handleLoadDefaults : undefined}
            onRemove={handleRemove}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onAddClick={() => setIsAdding(true)}
            disabled={disabled}
          />
        )}
      </PanelContent>
      <PanelFooter>
        <ButtonGroup>
          <Button variant='ghost' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleApply(lookupName, selectedLookups);
              handleClose();
            }}
            disabled={disabled || isAdding || !hasChanges}>
            Apply
          </Button>
        </ButtonGroup>
      </PanelFooter>
    </PanelContainer>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const SearchBarContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
`;

const FormButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 16px;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
`;

const TrashContainer = styled.div`
  color: ${colors.red100};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FormHeader = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const OlioValueCell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${colors.blue100};
  }
`;
